import React, { useState, useEffect } from "react";
import {
	Modal,
	Input,
	Select,
	Button,
	Table,
	Tag,
	message,
	Row,
	Col,
	Typography,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import useAxios from "../../utilities/useAxios";

const { Title, Text } = Typography;

const SearchMemberModal = ({
	visible,
	onCancel,
	onSelect,
	excludeUserId = null,
	apiPath = "/users/all-users/",
}) => {
	const [loading, setLoading] = useState(false);
	const [members, setMembers] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [searchCategory, setSearchCategory] = useState("name");
	const [filteredMembers, setFilteredMembers] = useState([]);

	useEffect(() => {
		if (visible) {
			fetchMembers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run only when visible
	}, [visible]);

	useEffect(() => {
		filterMembers();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- filterMembers depends on members, searchText, searchCategory
	}, [members, searchText, searchCategory]);

	const fetchMembers = async () => {
		try {
			setLoading(true);
			const requestConfig = {};
			if (apiPath === "/users/all-users/") {
				requestConfig.params = {
					include_inactive: "true",
				};
			}
			const response = await useAxios.get(apiPath, requestConfig);
			if (response.status === 200) {
				// Filter out the current user if excludeUserId is provided
				let filteredData = response.data;
				if (excludeUserId) {
					filteredData = response.data.filter(
						(member) =>
							member.id !==
							excludeUserId
					);
				}
				setMembers(filteredData);
			}
		} catch (error) {
			message.error("회원 목록을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const filterMembers = () => {
		if (!searchText) {
			setFilteredMembers(members);
			return;
		}

		const filtered = members.filter((member) => {
			let searchValue = "";
			switch (searchCategory) {
				case "name":
					searchValue = member.name || "";
					break;
				case "phone_number":
					searchValue = member.phone_number || "";
					break;
				case "affiliated_group":
					searchValue =
						member.cell_name ||
						member.serving_department_name ||
						member.affiliated_department_name ||
						"";
					break;
				default:
					searchValue = member.name || "";
			}
			return searchValue
				.toLowerCase()
				.includes(searchText.toLowerCase());
		});

		setFilteredMembers(filtered);
	};

	const handleSearch = (value) => {
		setSearchText(value);
	};

	const handleCategoryChange = (value) => {
		setSearchCategory(value);
	};

	const handleMemberSelect = (member) => {
		onSelect(member);
		onCancel();
	};

	const memberColumns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
			render: (name) => <Text strong>{name}</Text>,
		},
		{
			title: "성별",
			dataIndex: "gender",
			key: "gender",
			render: (gender) => (
				<Tag color={gender === "남" ? "blue" : "pink"}>
					{gender || "-"}
				</Tag>
			),
		},
		{
			title: "생년월일",
			dataIndex: "birthdate",
			key: "birthdate",
			render: (birthdate) =>
				birthdate
					? new Date(
							birthdate
					  ).toLocaleDateString("ko-KR")
					: "-",
		},
		{
			title: "연락처",
			dataIndex: "phone_number",
			key: "phone_number",
			render: (phone) => phone || "-",
		},
		{
			title: "구역",
			key: "district",
			render: (_, record) => {
				if (record.cell_name) {
					return `${record.district_name || ""} ${
						record.zone_name || ""
					} ${record.cell_name}`;
				}
				return "-";
			},
		},
		{
			title: "상태",
			dataIndex: "status_name",
			key: "status_name",
			render: (status) => (
				<Tag
					color={
						status === "입교인"
							? "blue"
							: status === "학습인"
							? "green"
							: status ===
							  "유아세례교인"
							? "orange"
							: status === "실종교인"
							? "red"
							: "default"
					}>
					{status || "-"}
				</Tag>
			),
		},
		{
			title: "선택",
			key: "action",
			render: (_, record) => (
				<Button
					type="primary"
					size="small"
					onClick={() =>
						handleMemberSelect(record)
					}>
					선택
				</Button>
			),
		},
	];

	return (
		<Modal
			title={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
					}}>
					<UserOutlined />
					<span>회원 검색</span>
				</div>
			}
			open={visible}
			onCancel={onCancel}
			footer={null}
			width="90%"
			style={{ top: 20 }}>
			<div style={{ marginBottom: 24 }}>
				<Title level={4}>
					<UserOutlined /> 회원 검색
				</Title>
				<Text type="secondary">
					인도자를 검색하여 선택하세요.
				</Text>

				{/* Search Section */}
				<Row
					gutter={[16, 16]}
					style={{
						marginTop: 16,
						marginBottom: 16,
					}}>
					<Col xs={24} sm={12} md={6}>
						<Select
							defaultValue="name"
							style={{
								width: "100%",
							}}
							onChange={
								handleCategoryChange
							}
							options={[
								{
									value: "name",
									label: "이름",
								},
								{
									value: "phone_number",
									label: "연락처",
								},
								{
									value: "affiliated_group",
									label: "소속",
								},
							]}
						/>
					</Col>
					<Col xs={24} sm={12} md={6}>
						<Input.Search
							placeholder="검색어를 입력하세요"
							allowClear
							onSearch={handleSearch}
							style={{
								width: "100%",
							}}
						/>
					</Col>
				</Row>

				{/* Member Table */}
				<Table
					columns={memberColumns}
					dataSource={filteredMembers}
					rowKey="id"
					loading={loading}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showQuickJumper: true,
					}}
					scroll={{ y: 400 }}
				/>
			</div>
		</Modal>
	);
};

export default SearchMemberModal;
